/*

Browser Service
===============

Checks if browser is supported, tracks window width and breakpoint


*/

import Emitter from './Emitter'
import { ORIENTATION_CHANGE, WINDOW_RESIZE } from '../events'

class Browser {
	init() {
		this.props = {
			mobileBreakpoint: 595,
			tabletBreakpoint: 1023
		}

		this.state = {
			width: this.calculateWidth(),
			height: this.calculateHeight(),
			browserInfo: this.getBrowserInfo(),
			orientation: this.getOrientation(),
			initialHeight: this.calculateHeight(),
			isTouch: this.checkIfTouch()
		}

		this.bindWindowResize()
		this.bindOrientationChange()
		this.checkIfSupported()

		if (this.state.isTouch) {
			document.body.classList.add('has-touch')
		} else {
			document.body.classList.add('no-touch')
		}
	}

	setSizes() {
		let newWidth = this.calculateWidth()
		let newHeight = this.calculateHeight()
		Emitter.emit(WINDOW_RESIZE, {
			width: newWidth,
			height: newHeight
		})
		this.state.height = newHeight
		this.state.width = newWidth
	}

	setOrientation() {
		this.state.orientation = this.getOrientation()
		Emitter.emit(ORIENTATION_CHANGE, this.state.orientation)
	}

	calculateWidth() {
		return window.outerWidth ? window.outerWidth : window.innerWidth
	}

	calculateHeight() {
		return window.outerHeight ? window.outerHeight : window.innerHeight
	}

	calculatePageHeight() {
		const D = document
		return Math.max(
			D.body.scrollHeight,
			D.documentElement.scrollHeight,
			D.body.offsetHeight,
			D.documentElement.offsetHeight,
			D.body.clientHeight,
			D.documentElement.clientHeight
		)
	}

	pixelRatio() {
		let canvas = document.createElement('canvas')
		let ctx = canvas.getContext('2d')
		let dpr = window.devicePixelRatio || 1
		let bsr =
			ctx.webkitBackingStorePixelRatio ||
			ctx.mozBackingStorePixelRatio ||
			ctx.msBackingStorePixelRatio ||
			ctx.oBackingStorePixelRatio ||
			ctx.backingStorePixelRatio ||
			1

		return dpr / bsr
	}

	getOrientation() {
		switch (window.orientation) {
			case -90 || 90:
				return 'landscape'
				break
			default:
				return 'portrait'
				break
		}
	}

	checkIfTouch() {
		var prefixes = ' -webkit- -moz- -o- -ms- '.split(' ')
		var mq = function(query) {
			return window.matchMedia(query).matches
		}

		if (
			'ontouchstart' in window ||
			(window.DocumentTouch && document instanceof DocumentTouch)
		) {
			return true
		}

		// include the 'heartz' as a way to have a non matching MQ to help terminate the join
		// https://git.io/vznFH
		var query = ['(', prefixes.join('touch-enabled),('), 'heartz', ')'].join('')
		return mq(query)
	}

	isTouch() {
		return this.state.isTouch
	}

	isIOS() {
		return this.state.browserInfo.name === 'ios'
	}

	width() {
		return this.state.width
	}

	height() {
		return this.state.height
	}

	initialHeight() {
		return this.state.initialHeight
	}

	orientation() {
		return this.state.orientation
	}

	pageHeight() {
		return this.calculatePageHeight()
	}

	isMobile() {
		return this.state.width <= this.props.mobileBreakpoint
	}

	isTablet() {
		return this.state.width <= this.props.tabletBreakpoint
	}

	isDesktop() {
		return this.state.width > this.props.tabletBreakpoint
	}

	hasTouch() {
		if (typeof window !== 'undefined') {
			return Boolean(
				'ontouchstart' in window ||
					window.navigator.maxTouchPoints > 0 ||
					window.navigator.msMaxTouchPoints > 0 ||
					(window.DocumentTouch && document instanceof DocumentTouch)
			)
		}
	}

	hasAnimationSupport() {
		let animation = false,
			animationstring = 'animation',
			keyframeprefix = '',
			domPrefixes = 'Webkit Moz O ms Khtml'.split(' '),
			pfx = '',
			elm = document.createElement('div')

		if (elm.style.animationFillMode !== undefined) {
			return true
		}

		if (animation === false) {
			for (let i = 0; i < domPrefixes.length; i++) {
				if (elm.style[domPrefixes[i] + 'AnimationFillMode'] !== undefined) {
					return true
				}
			}
		}

		return false
	}

	bindWindowResize() {
		window.addEventListener('resize', this.setSizes.bind(this))
	}

	bindOrientationChange() {
		window.addEventListener('orientationchange', this.setOrientation.bind(this))
	}

	getBrowserInfo() {
		const browsers = [
			['edge', /Edge\/([0-9\._]+)/],
			['yandexbrowser', /YaBrowser\/([0-9\._]+)/],
			['chrome', /(?!Chrom.*OPR)Chrom(?:e|ium)\/([0-9\.]+)(:?\s|$)/],
			['crios', /CriOS\/([0-9\.]+)(:?\s|$)/],
			['firefox', /Firefox\/([0-9\.]+)(?:\s|$)/],
			['opera', /Opera\/([0-9\.]+)(?:\s|$)/],
			['opera', /OPR\/([0-9\.]+)(:?\s|$)$/],
			['ie', /Trident\/7\.0.*rv\:([0-9\.]+)\).*Gecko$/],
			['ie', /MSIE\s([0-9\.]+);.*Trident\/[4-7].0/],
			['ie', /MSIE\s(7\.0)/],
			['bb10', /BB10;\sTouch.*Version\/([0-9\.]+)/],
			['android', /Android\s([0-9\.]+)/],
			['ios', /Version\/([0-9\._]+).*Mobile.*Safari.*/],
			['safari', /Version\/([0-9\._]+).*Safari/]
		]

		const userAgentString = navigator.userAgent
		const result = browsers
			.map(function(rule) {
				if (rule[1].test(userAgentString)) {
					let match = rule[1].exec(userAgentString)
					let version = match && match[1].split(/[._]/).slice(0, 3)

					if (version && version.length < 3) {
						Array.prototype.push.apply(version, version.length == 1 ? [0, 0] : [0])
					}

					let versionDecimal = ''

					version.map((item, index) => {
						if (index == 1) {
							versionDecimal += '.'
						}

						versionDecimal += item
					})

					return {
						name: rule[0],
						version: versionDecimal
					}
				}
			})
			.filter(Boolean)
			.shift()

		return result
	}

	checkIfSupported() {
		const supported = {
			edge: 1,
			ie: 11,
			chrome: 30,
			firefox: 35,
			safari: 6,
			ios: 7,
			android: 4.3
		}

		const browser = this.getBrowserInfo()

		// fix facebook bug
		if (!browser || !browser.name || !browser.version) return

		if (supported[browser.name] !== undefined && browser.version < supported[browser.name]) {
			document.body.className += ' not-supported'
		}
	}
}

let b = new Browser()

window.addEventListener('DOMContentLoaded', () => {
	b.init()
})

export default b
